import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AuthenticationService } from '../auth/authentication.service';
import { TokenStorage } from '../auth/token-storage.service';
import { Router } from '@angular/router';
import { HttpStatusCode } from '../../app.enums';
import { isNullOrUndefined } from '../../content/pages/components/general/event/shared/validator/undefined-or-null';

@Injectable({
  providedIn: 'root',
})
export class JwtTokenInterceptor implements HttpInterceptor {
  private authService = inject(AuthenticationService);
  private tokenStorage = inject(TokenStorage);
  private router = inject(Router);


  isRefreshingToken: boolean = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    // 1. Skip interception for the refresh endpoint itself to prevent recursive loops
    if (this.authService.verifyTokenRequest(request.url)) {
      return next.handle(request);
    }

    // 2. Skip interception for Azure Blob Storage / local storage emulator / SAS upload requests
    try {
      const isAbsoluteUrl = request.url.startsWith('https://');
      if (isAbsoluteUrl) {
        const parsedUrl = new URL(request.url);
        const host = parsedUrl.hostname;
        const port = parsedUrl.port;
        
        const isAzureOrEmulator = host.endsWith('blob.core.windows.net') || 
                                  ((host === '127.0.0.1' || host === 'localhost') && port === '10000');
                                  
        const isSasUrl = parsedUrl.searchParams.has('sig');

        if (isAzureOrEmulator || isSasUrl) {
          return next.handle(request);
        }
      }
    } catch (e) {
      // fallback check
    }

    return next.handle(this.addTokenToRequest(request)).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          // Break cycle if backend explicitly forbids retry
          if (err?.headers?.has('X-Do-Not-Retry') || err?.headers?.has('Token-Invalid-Structural')) {
            this.authService.logout(false);
            return throwError(() => err);
          }

          switch (err.status) {
            case HttpStatusCode.UNAUTHORIZED: {
              return this.handle401Error(request, next);
            }
            case HttpStatusCode.NOT_FOUND: {
              this.router.navigate(['/404']);
              break;
            }
            case HttpStatusCode.INTERNAL_SERVER_ERROR: {
              if (this.authService.verifyTokenRequest(err.url)) {
                this.authService.logout(true);
              }
              return throwError(() => err);
            }
            case HttpStatusCode.FORBIDDEN: {
              this.router.navigate(['/403']);
              break;
            }
            case HttpStatusCode.UNPROCESSABLE_ENTITY: {
              if (err.error && err.error.isSuspended) {
                sessionStorage.setItem('suspendedUserData', JSON.stringify(err.error));
                this.authService.logout(true);
              }
              break;
            }
          }
          return throwError(() => err);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * The function adds an authorization token to an HTTP request, and if the request URL includes
   * 'GetCommonDetails' and there is no token, it clears the token storage, navigates to the login page,
   * and reloads the page.
   * @returns an instance of `HttpRequest<any>`.
   */
  private addTokenToRequest(request: HttpRequest<any>): HttpRequest<any> {
    let token: string = this.tokenStorage.getAccessTokens();
    if (request && request.url && request.url.includes('GetCommonDetails') && !token) {
      this.tokenStorage.clear();
      this.router.navigate(['/auth/login']).then(() => {
        window.location.reload();
      });
      return new Observable() as any;
    }
    if (this.isValidToken(token)) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return request;
  }

  private isValidToken(token: any): boolean {
    return (
      !isNullOrUndefined(token) &&
      token !== '' &&
      token !== 'undefined' &&
      token !== 'null' &&
      typeof token === 'string'
    );
  }

  // RATIONALE: We use the Single-Flight pattern to handle multiple concurrent 401s.
  // 1. The first 401 triggers the refresh and sets isRefreshingToken = true.
  // 2. Subsequent 401s see isRefreshingToken is true and wait on the tokenSubject.
  // 3. This prevents "Refresh Flooding" and traffic spikes.
  // 4. If refresh fails, we logout and error out all waiting requests to break the loop.
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (request.headers.has('X-Request-Retried')) {
      this.authService.logout(false);
      return throwError(() => new Error('Request already retried once'));
    }

    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      // Reset the subject so subsequent requests wait
      this.tokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((result: any) => {
          const newToken = result?.data?.accessToken;
          if (newToken) {
            this.tokenSubject.next(newToken);
            const retriedRequest = this.addTokenToRequest(request).clone({
              setHeaders: { 'X-Request-Retried': 'true' }
            });
            return next.handle(retriedRequest);
          }
          throw new Error('No token returned from refresh');
        }),
        catchError(err => {
          const currentLocalToken = this.tokenStorage.getAccessTokens();
          const authHeader = request.headers.get('Authorization');
          const failedToken = authHeader && authHeader.startsWith('Bearer ') ? authHeader.substring(7) : null;
          
          // If another tab has already refreshed and stored a new token, recover!
          if (currentLocalToken && currentLocalToken !== failedToken) {
            this.tokenSubject.next(currentLocalToken);
            const recoveredRequest = this.addTokenToRequest(request).clone({
              setHeaders: { 'X-Request-Retried': 'true' }
            });
            return next.handle(recoveredRequest);
          }

          // Otherwise, notify waiting subscribers of the failure and reset the subject
          const oldSubject = this.tokenSubject;
          this.tokenSubject = new BehaviorSubject<string>(null);
          oldSubject.error(err);

          this.authService.logout(false);
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
    } else {
      // If already refreshing, wait for the new token and then retry
      return this.tokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(() => {
          const retriedRequest = this.addTokenToRequest(request).clone({
            setHeaders: { 'X-Request-Retried': 'true' }
          });
          return next.handle(retriedRequest);
        }),
        catchError(err => {
          // If the refresh failed, also fail this waiting request
          return throwError(() => err);
        })
      );
    }
  }
}
